import { useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { safeDecodeToken } from "../utils/tokenUtils";
import { normalizeEmployeeState, extractCompanyName, extractEmployeesFromPayload } from "../utils/employeeUtils";
import { collectHierarchyEmployees } from "../utils/hierarchyUtils";
import { buildDepartmentStatsFromEmployees, mapDepartmentStatsResponse } from "../utils/departmentStatsUtils";

const useMetricsFetch = ({
    BASE_URL,
    viewType,
    hasAdminLevel,
    setEmployeeCount,
    setCounts,
    setDepartmentStats,
}) => {
    useEffect(() => {
        let canceled = false;

        const fetchMetrics = async () => {
            try {
                if (!BASE_URL) return;

                const token = localStorage.getItem("token");
                if (!token) return;

                const payload = safeDecodeToken(token) || {};
                const roleCandidates = [
                    payload.role,
                    payload.Role,
                    localStorage.getItem("role"),
                    localStorage.getItem("userRole"),
                ].filter((value) => typeof value === "string" && value.trim().length > 0);

                const normalizedRole = roleCandidates.length
                    ? String(roleCandidates[0]).trim().toLowerCase()
                    : "";

                const managerInfinityCode =
                    payload.infinityCode || localStorage.getItem("infinityCode") || "";
                const clientNameRaw =
                    localStorage.getItem("clientName") ||
                    payload.clientName ||
                    payload.client ||
                    payload.companyName ||
                    "Infinity";
                const clientName =
                    typeof clientNameRaw === "string" && clientNameRaw.trim().length
                        ? clientNameRaw.trim()
                        : "Infinity";
                const encodedClientName = encodeURIComponent(clientName);
                const adminViewActive = viewType === "admin" && hasAdminLevel();
                const shouldUseHierarchy =
                    !adminViewActive && (normalizedRole === "manager" || viewType === "manager");

                if (adminViewActive && normalizedRole === "manager") {
                    const adminLevelRaw = localStorage.getItem("adminLevel");
                    let visibilityClientName = clientName;

                    if (adminLevelRaw) {
                        try {
                            const parsedAdminLevel = JSON.parse(adminLevelRaw);
                            if (parsedAdminLevel?.clientName) {
                                visibilityClientName = parsedAdminLevel.clientName;
                            }
                        } catch (parseError) {
                            console.warn("Failed to parse admin level for visibility fetch:", parseError);
                        }
                    }

                    // fields=basic: the metrics below only read employeeState,
                    // workingCompany, department.name and position — all in
                    // the basic select. Avoids pulling ~87 columns per
                    // employee just to compute four counters.
                    const query = visibilityClientName
                        ? `?clientName=${encodeURIComponent(visibilityClientName)}&fields=basic`
                        : "?fields=basic";

                    const visibilityResponse = await apiFetch(
                        `${BASE_URL}/employees/visibility${query}`,
                        { method: "GET", headers: { "x-view-type": "admin" } }
                    );

                    if (!visibilityResponse.ok) {
                        throw new Error("Failed to fetch visibility-scoped employees for admin view");
                    }

                    const visibilityPayload = await visibilityResponse.json();
                    const visibleEmployees = extractEmployeesFromPayload(visibilityPayload);

                    if (canceled) return;

                    const activeCount = visibleEmployees.filter(
                        (emp) => normalizeEmployeeState(emp) === "active"
                    ).length;
                    const onLeaveCount = visibleEmployees.filter(
                        (emp) => normalizeEmployeeState(emp) === "onleave"
                    ).length;
                    const companies = new Set(
                        visibleEmployees.map(extractCompanyName).filter(Boolean)
                    );
                    const totalCompaniesCount = companies.size || (visibleEmployees.length ? 1 : 0);

                    setEmployeeCount(visibleEmployees.length);
                    setCounts({
                        Active: activeCount,
                        OnLeave: onLeaveCount,
                        Recent: 0,
                        TotalCompanies: totalCompaniesCount,
                    });
                    setDepartmentStats(
                        buildDepartmentStatsFromEmployees(visibleEmployees, visibleEmployees.length)
                    );
                    return;
                }

                if (shouldUseHierarchy) {
                    if (!managerInfinityCode) {
                        if (!canceled) {
                            setEmployeeCount(0);
                            setCounts({ Active: 0, OnLeave: 0, Recent: 0, TotalCompanies: 0 });
                            setDepartmentStats([]);
                        }
                        return;
                    }

                    const hierarchyUrl = `${BASE_URL}/employees/clients/${encodedClientName}/hierarchy?managerInfinityCode=${encodeURIComponent(managerInfinityCode)}`;
                    const hierarchyResponse = await apiFetch(hierarchyUrl, { method: "GET" });

                    if (!hierarchyResponse.ok) {
                        throw new Error("Failed to fetch manager hierarchy data");
                    }

                    const hierarchyPayload = await hierarchyResponse.json();
                    const subordinates = collectHierarchyEmployees(
                        hierarchyPayload,
                        managerInfinityCode
                    );

                    console.log(`👥 Manager hierarchy employees resolved: ${subordinates.length}`);

                    if (canceled) return;

                    const activeCount = subordinates.filter(
                        (emp) => normalizeEmployeeState(emp) === "active"
                    ).length;
                    const onLeaveCount = subordinates.filter(
                        (emp) => normalizeEmployeeState(emp) === "onleave"
                    ).length;
                    const companies = new Set(subordinates.map(extractCompanyName).filter(Boolean));
                    const totalCompaniesCount = companies.size || (subordinates.length ? 1 : 0);

                    setEmployeeCount(subordinates.length);
                    setCounts({
                        Active: activeCount,
                        OnLeave: onLeaveCount,
                        Recent: 0,
                        TotalCompanies: totalCompaniesCount,
                    });
                    setDepartmentStats(buildDepartmentStatsFromEmployees(subordinates, subordinates.length));
                    return;
                }

                const isInfinityClient = clientName.toLowerCase() === "infinity";

                const employeeCountUrl = isInfinityClient
                    ? `${BASE_URL}/employees/count`
                    : `${BASE_URL}/employees/client/${encodedClientName}/count`;
                const activeUrl = isInfinityClient
                    ? `${BASE_URL}/employees/Active`
                    : `${BASE_URL}/employees/client/${encodedClientName}/Active`;
                const onLeaveUrl = isInfinityClient
                    ? `${BASE_URL}/employees/OnLeave`
                    : `${BASE_URL}/employees/client/${encodedClientName}/OnLeave`;
                const deptStatsUrl = isInfinityClient
                    ? `${BASE_URL}/employees/departments/stats`
                    : `${BASE_URL}/employees/client/${encodedClientName}/departments/stats`;

                const [employeeCountRes, activeRes, onLeaveRes, deptStatsRes] = await Promise.all([
                    apiFetch(employeeCountUrl, { method: "GET" }),
                    apiFetch(activeUrl, { method: "GET" }),
                    apiFetch(onLeaveUrl, { method: "GET" }),
                    apiFetch(deptStatsUrl, { method: "GET" }),
                ]);

                if (!employeeCountRes.ok || !activeRes.ok || !onLeaveRes.ok || !deptStatsRes.ok) {
                    throw new Error("Failed to fetch dashboard metrics");
                }

                const [employeeCountData, activeData, onLeaveData, deptStatsData] = await Promise.all([
                    employeeCountRes.json(),
                    activeRes.json(),
                    onLeaveRes.json(),
                    deptStatsRes.json(),
                ]);

                if (canceled) return;

                const totalCount = Number(employeeCountData?.count) || 0;

                setEmployeeCount(totalCount);
                setCounts((prev) => ({
                    Active: Number(activeData?.count) || 0,
                    OnLeave: Number(onLeaveData?.count) || 0,
                    Recent: 7,
                    TotalCompanies:
                        prev?.TotalCompanies && prev.TotalCompanies > 0
                            ? prev.TotalCompanies
                            : totalCount > 0
                                ? 1
                                : 0,
                }));
                setDepartmentStats(mapDepartmentStatsResponse(deptStatsData, totalCount));
            } catch (error) {
                console.error("Error fetching dashboard metrics:", error);
                if (!canceled) {
                    setDepartmentStats([]);
                }
            }
        };

        fetchMetrics();
        return () => {
            canceled = true;
        };
    }, [BASE_URL, viewType]);
};

export default useMetricsFetch;